import { Box, Card, Flex, Heading, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal, createDataCollectionContext, dbTimeToUserTime, toaster, useDrawer, useMobileContext, type ConfirmModalHandle } from '@shared';
import { useMemo, useRef } from 'react';
import { createColumnHelper, type Table } from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';
import { useDeleteMultipleUsers } from '../../api/hooks/useUserSuperadmin';
import usersService from '../../api/services/usersService';
import SuperadminUserDrawer from './SuperadminUserDrawer';
import { useGetAllAccounts } from '../../api/hooks/useAccountSuperadmin';

const columnHelper = createColumnHelper<User>();
const TIMEZONE = import.meta.env.VITE_DEFAULT_TIMEZONE as string;

export default function Users() {
	const { t } = useTranslation();
	const { data: accounts } = useGetAllAccounts();

	const { DataCollectionContextProvider, DataCollectionTable, DataCollectionTableFooter, DataCollectionTableHeader } = useMemo(() => createDataCollectionContext<User>(), []);
	const queryClient = useQueryClient();
	const { isMobile } = useMobileContext();
	const { openDrawer } = useDrawer();

	const columns = [
		columnHelper.accessor('userID', {
			header: t('general.id'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('firstName', {
			header: t('general.first_name'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('lastName', {
			header: t('general.last_name'),
			sortingFn: 'basic',
		}),
		columnHelper.accessor('email', {
			header: t('general.email'),
			sortingFn: 'basic',
		}),
		columnHelper.display({
			id: 'userAccounts',
			enableSorting: false,
			header: t('general.accounts'),
			cell: ({ row }) => {
				const accountNames = row.original.userAccounts?.map(userAccount => {
					const accountName = accounts?.data.accounts.find(account => account.accountID === userAccount.accountID)?.name;
					return accountName;
				});
				return accountNames?.join(', ');
			},
		}),
		columnHelper.accessor('phone', {
			header: t('general.phone'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text userSelect={'text'}>{row.original.phone}</Text>;
			},
		}),
		columnHelper.accessor('timeLatestLogin', {
			header: t('general.time_latest_login'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeLatestLogin) {
					return <Text>{t('general.never_logged_in')}</Text>;
				}
				return <Text>{dbTimeToUserTime(row.original.timeLatestLogin, TIMEZONE)}</Text>;
			},
		}),
		columnHelper.accessor('timeCreated', {
			header: t('general.time_created'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				return <Text>{dbTimeToUserTime(row.original.timeCreated, TIMEZONE)}</Text>;
			},
		}),
		columnHelper.accessor('timeModified', {
			header: t('general.time_modified'),
			sortingFn: 'basic',
			cell: ({ row }) => {
				if (!row.original.timeModified) {
					return null;
				}
				return <Text>{dbTimeToUserTime(row.original.timeModified, TIMEZONE)}</Text>;
			},
		}),
	];

	const { mutate: deleteMultipleUsers } = useDeleteMultipleUsers();

	const confirmRemoveRef = useRef<ConfirmModalHandle>(null);

	const selectedItemsActions = [
		{
			id: 'deleteMany',
			label: t('general.delete_selected'),
			callbackFn: async (ids: Array<string>, table: Table<User>) => {
				const payload = {
					users: ids.map(id => {
						return {
							userID: parseInt(id),
							userAccount: {
								delete: true,
							},
						};
					}),
				};

				const response = await confirmRemoveRef.current?.open({
					title: t('general.remove_users'),
					content: t('general.really_remove_users'),
					confirmButtonText: t('general.remove'),
					confirmButtonVariant: 'subtle',
				});
				if (response === true) {
					deleteMultipleUsers(payload, {
						onSuccess: () => {
							toaster.create({
								type: 'success',
								title: t('general.removed'),
							});
							queryClient.invalidateQueries({ queryKey: ['users'] });
							table.resetRowSelection();
						},
					});
				}
			},
		},
	];

	return (
		<Flex flexDirection={'column'} gap={5} width={'100%'} alignItems={'center'} height="100%">
			{!isMobile ? (
				<Card.Root maxWidth={'2000px'} width={'100%'}>
					<Card.Body>
						<Heading>{t('general.users')}</Heading>
					</Card.Body>
				</Card.Root>
			) : null}

			<Flex overflow="auto" width={'100%'} display={'flex'} flexDirection={'column'} height={'100%'} maxW={'2000px'}>
				<Flex display={'flex'} flex={1} overflow={'auto'}>
					<Card.Root flex={1} display="flex">
						<Card.Body display="flex" flexDirection="column" padding={5} gap={5} overflow={isMobile ? 'initial' : 'auto'}>
							<DataCollectionContextProvider
								dataKey="users"
								keyAttribute="userID"
								emptyDataText={t('general.no_users_found')}
								fetchFn={(...args) => usersService.getAllResources(...args)}
								queryParams={{ getRelated: 'userAccounts', scopeWith: 'timeLatestLogin' }}
								selectedItemsActions={selectedItemsActions}
								initialItemsPerPage={25}
								columns={columns}
								defaultHiddenColumns={[]}
								isServerSide={true}
								isSelectable={false}
								defaultSort={{ id: 'timeLatestLogin', desc: true }}
								onClickRow={row => {
									openDrawer({
										id: 'userDrawer',
										content: <SuperadminUserDrawer userID={row.original.userID} />,
									});
								}}
							>
								<Flex gap={2} flexWrap="wrap" flexShrink={0} ml="auto">
									<DataCollectionTableHeader />
								</Flex>
								<Box flex={1} overflow="auto" minH={0}>
									<DataCollectionTable />
								</Box>
								<Flex gap={2} flexShrink={0}>
									<DataCollectionTableFooter />
									<ConfirmModal ref={confirmRemoveRef} />
								</Flex>
							</DataCollectionContextProvider>
						</Card.Body>
					</Card.Root>
				</Flex>
			</Flex>
		</Flex>
	);
}
